import { z } from 'zod';

export const SuporteOsStatusSchema = z.enum([
  'ABERTA',
  'EM_ATENDIMENTO',
  'AGUARDANDO_PECA',
  'CONCLUIDA',
  'CANCELADA',
  'UNKNOWN',
]);

export type SuporteOsStatus = z.infer<typeof SuporteOsStatusSchema>;

export const SuporteOsTipoSchema = z.enum([
  'CORRETIVA',
  'PREVENTIVA',
  'INSTALACAO',
  'REMOCAO',
  'UNKNOWN',
]);

export type SuporteOsTipo = z.infer<typeof SuporteOsTipoSchema>;

export const SuporteOsPrioridadeSchema = z.enum(['BAIXA', 'NORMAL', 'ALTA', 'URGENTE']);

export type SuporteOsPrioridade = z.infer<typeof SuporteOsPrioridadeSchema>;

export const SuporteKpisSchema = z.object({
  osAbertas: z.number().int().nonnegative(),
  emAtendimento: z.number().int().nonnegative(),
  aguardandoPeca: z.number().int().nonnegative(),
  concluidasHoje: z.number().int().nonnegative(),
  tempoMedioResolucaoMinutos: z.number().int().nonnegative().nullable(),
  semPreventiva: z.number().int().nonnegative(),
});

export type SuporteKpis = z.infer<typeof SuporteKpisSchema>;

export const SuporteOrdemRowSchema = z.object({
  serviceOrderId: z.string(),
  serviceOrderCode: z.string(),
  serialNumber: z.string().nullable(),
  empresaFilial: z.string().nullable(),
  tipo: SuporteOsTipoSchema,
  tipoLabel: z.string(),
  tipoChip: z.string(),
  prioridade: SuporteOsPrioridadeSchema,
  prioridadeLabel: z.string(),
  prioridadeChip: z.string(),
  tecnicoNome: z.string().nullable(),
  tecnicoLogin: z.string().nullable(),
  agendadoPara: z.string().datetime().nullable(),
  agendadoDisplay: z.string().nullable(),
  status: SuporteOsStatusSchema,
  statusLabel: z.string(),
  statusChip: z.string(),
  diagnosticoIA: z.string().nullable(),
  credenciadaName: z.string().nullable(),
});

export type SuporteOrdemRow = z.infer<typeof SuporteOrdemRowSchema>;

export const SuporteBaseMembroSchema = z.object({
  login: z.string(),
  nome: z.string(),
  statusLabel: z.string(),
  statusChip: z.string(),
  disponivel: z.boolean(),
});

export type SuporteBaseMembro = z.infer<typeof SuporteBaseMembroSchema>;

export const SuporteBaseCardSchema = z.object({
  baseId: z.string(),
  nome: z.string(),
  uf: z.string().nullable(),
  tecnicosAtivos: z.number().int().nonnegative(),
  membros: z.array(SuporteBaseMembroSchema),
  outrosCount: z.number().int().nonnegative(),
});

export type SuporteBaseCard = z.infer<typeof SuporteBaseCardSchema>;

export const SuporteSemPreventivaRowSchema = z.object({
  serialNumber: z.string(),
  empresaFilial: z.string().nullable(),
  tipoEquipamento: z.string().nullable(),
  diasSemPreventiva: z.number().int().nonnegative(),
  ultimaVisita: z.string().nullable(),
  ultimaVisitaDisplay: z.string().nullable(),
  tecnicoReferencia: z.string().nullable(),
});

export type SuporteSemPreventivaRow = z.infer<typeof SuporteSemPreventivaRowSchema>;

export const SuporteMetaSchema = z.object({
  generatedAt: z.string().datetime(),
  provider: z.enum(['mock', 'iclass-v2']),
  clusterName: z.string().nullable(),
  dateRange: z.object({ from: z.string().datetime(), to: z.string().datetime() }),
  preventivaThresholdDays: z.number().int().positive(),
});

export type SuporteMeta = z.infer<typeof SuporteMetaSchema>;

export const SuporteTecnicoResponseSchema = z.object({
  kpis: SuporteKpisSchema,
  ordens: z.array(SuporteOrdemRowSchema),
  bases: z.array(SuporteBaseCardSchema),
  semPreventiva: z.array(SuporteSemPreventivaRowSchema),
  meta: SuporteMetaSchema,
});

export type SuporteTecnicoResponse = z.infer<typeof SuporteTecnicoResponseSchema>;

export const CreateSuporteOsSchema = z.object({
  serialNumber: z.string().min(1),
  tipo: SuporteOsTipoSchema.exclude(['UNKNOWN']),
  prioridade: SuporteOsPrioridadeSchema,
  descricao: z.string().min(10).max(4000),
  teamLogin: z.string().optional(),
  agendadoPara: z.string().datetime().optional(),
  diagnosticoIA: z.string().max(2000).optional(),
  customerCode: z.string().optional(),
  addressCode: z.string().optional(),
  soTypeSummary: z.string().optional(),
});

export type CreateSuporteOs = z.infer<typeof CreateSuporteOsSchema>;

export const CreateSuporteOsResponseSchema = z.object({
  serviceOrderId: z.string(),
  serviceOrderCode: z.string(),
  serialNumber: z.string(),
  status: SuporteOsStatusSchema,
  statusLabel: z.string(),
  message: z.string(),
});

export type CreateSuporteOsResponse = z.infer<typeof CreateSuporteOsResponseSchema>;
